import * as React from 'react';
import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import Spinner from '@components/Spinner';
import FeatherIcon from 'feather-icons-react';
import generateKey from '@utils/generate-key';
import { formatMinutes, formatSeconds } from '@utils/format-time';
import { getApp } from 'firebase/app';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';

type VoiceCloningFile = {
  id: string;
  fileName: string;
  storagePath: string;
  downloadURL: string;
  fileSize: number;
  duration: number;
  mimeType: string;
  createdAt: Timestamp;
};

type Props = {
  user: any;
  db: any;
  embedded?: boolean;
};

const MAX_FILES = 5;
const MAX_FILE_SIZE = 50 * 1024 * 1024;
const MAX_DURATION = 300;
const ACCEPTED_TYPES = ['audio/wav', 'audio/x-wav', 'audio/mpeg', 'audio/mp3'];
const ACCEPTED_EXTENSIONS = ['.wav', '.mp3'];

function getFileExtension(fileName: string): string {
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex >= 0 ? fileName.substring(dotIndex).toLowerCase() : '';
}

function getAudioDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    const url = URL.createObjectURL(file);

    const cleanup = () => URL.revokeObjectURL(url);

    audio.addEventListener(
      'loadedmetadata',
      () => {
        if (audio.duration === Infinity) {
          audio.currentTime = 1e101;
          audio.addEventListener(
            'durationchange',
            () => {
              cleanup();
              resolve(audio.duration);
            },
            { once: true }
          );
        } else {
          cleanup();
          resolve(audio.duration);
        }
      },
      { once: true }
    );

    audio.addEventListener(
      'error',
      () => {
        cleanup();
        reject(new Error('Could not read audio file'));
      },
      { once: true }
    );

    audio.src = url;
  });
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${formatMinutes(mins)}:${formatSeconds(secs)}`;
}

export default function VoiceCloningUpload({ user, db, embedded }: Props) {
  const { t } = useTranslation('dashboard');
  const [files, setFiles] = React.useState<VoiceCloningFile[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = React.useState(true);
  const [uploadingFileName, setUploadingFileName] = React.useState<
    string | null
  >(null);
  const [deletingFileId, setDeletingFileId] = React.useState<string | null>(
    null
  );
  const [playingFileId, setPlayingFileId] = React.useState<string | null>(null);
  const [error, setError] = React.useState('');
  const [successMessage, setSuccessMessage] = React.useState('');
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const mountedRef = React.useRef(true);

  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const storage = React.useMemo(
    () => getStorage(getApp(), process.env.REACT_APP_FIREBASE_BUCKET),
    []
  );

  React.useEffect(() => {
    async function loadFiles() {
      if (!user) return;
      try {
        const colRef = collection(
          db,
          `users/${user.uid}/voiceCloningFiles`
        );
        const q = query(colRef, orderBy('createdAt', 'asc'));
        const snapshot = await getDocs(q);
        const loadedFiles: VoiceCloningFile[] = snapshot.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<VoiceCloningFile, 'id'>),
        }));
        if (mountedRef.current) {
          setFiles(loadedFiles);
          setIsLoadingFiles(false);
        }
      } catch (err) {
        console.error('Failed to load voice cloning files:', err);
        if (mountedRef.current) {
          setIsLoadingFiles(false);
        }
      }
    }
    loadFiles();
  }, [user, db]);

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => {
      if (mountedRef.current) setSuccessMessage('');
    }, 3000);
  };

  const showError = (message: string) => {
    setError(message);
    setTimeout(() => {
      if (mountedRef.current) setError('');
    }, 5000);
  };

const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0];
  if (!file) return;
  e.target.value = '';
    setError('');

    if (files.length >= MAX_FILES) {
      showError(t('voiceCloning.errors.maxFiles'));
      return;
    }

    const ext = getFileExtension(file.name);
    if (
      !ACCEPTED_TYPES.includes(file.type) &&
      !ACCEPTED_EXTENSIONS.includes(ext)
    ) {
      showError(t('voiceCloning.errors.invalidFormat'));
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      showError(t('voiceCloning.errors.fileTooLarge'));
      return;
    }

    let duration: number;
    try {
      duration = await getAudioDuration(file);
    } catch {
      showError(t('voiceCloning.errors.readFailed'));
      return;
    }

    if (duration > MAX_DURATION) {
      showError(t('voiceCloning.errors.tooLong'));
      return;
    }

    setUploadingFileName(file.name);

    try {
      const uuid = generateKey();
      const fileExt = ext || '.wav';
      const storagePath = `users/${user.uid}/voice-cloning/${uuid}${fileExt}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, file, { contentType: file.type });
      const downloadURL = await getDownloadURL(storageRef);

      const colRef = collection(
        db,
        `users/${user.uid}/voiceCloningFiles`
      );
      const docRef = await addDoc(colRef, {
        fileName: file.name,
        storagePath,
        downloadURL,
        fileSize: file.size,
        duration,
        mimeType: file.type,
        createdAt: Timestamp.now(),
      });

      if (mountedRef.current) {
        setFiles((prev) => [
          ...prev,
          {
            id: docRef.id,
            fileName: file.name,
            storagePath,
            downloadURL,
            fileSize: file.size,
            duration,
            mimeType: file.type,
            createdAt: Timestamp.now(),
          },
        ]);
        showSuccess(t('voiceCloning.uploadSuccess'));
      }
    } catch (err) {
      console.error('Upload failed:', err);
      if (mountedRef.current) {
        showError(t('voiceCloning.errors.uploadFailed'));
      }
    } finally {
      if (mountedRef.current) {
        setUploadingFileName(null);
      }
    }
  };

  const handleDelete = async (fileToDelete: VoiceCloningFile) => {
    if (playingFileId === fileToDelete.id) {
      audioRef.current?.pause();
      setPlayingFileId(null);
    }

    setDeletingFileId(fileToDelete.id);

    try {
      const storageRef = ref(storage, fileToDelete.storagePath);
      await deleteObject(storageRef);
      await deleteDoc(
        doc(db, `users/${user.uid}/voiceCloningFiles`, fileToDelete.id)
      );

      if (mountedRef.current) {
        setFiles((prev) => prev.filter((f) => f.id !== fileToDelete.id));
        showSuccess(t('voiceCloning.deleteSuccess'));
      }
    } catch (err) {
      console.error('Delete failed:', err);
      if (mountedRef.current) {
        showError(t('voiceCloning.errors.deleteFailed'));
      }
    } finally {
      if (mountedRef.current) {
        setDeletingFileId(null);
      }
    }
  };

  const handlePlayPause = (file: VoiceCloningFile) => {
    if (!audioRef.current) return;

    if (playingFileId === file.id) {
      audioRef.current.pause();
      setPlayingFileId(null);
    } else {
      audioRef.current.src = file.downloadURL;
      audioRef.current.play();
      setPlayingFileId(file.id);
    }
  };

  const handleAudioEnded = () => {
    setPlayingFileId(null);
  };

  const isUploading = uploadingFileName !== null;
  const canUpload = files.length < MAX_FILES && !isUploading;

  return (
    <div className={`${styles.container} ${embedded ? styles.embedded : ''}`}>
      <p className={styles.description}>{t('voiceCloning.description')}</p>

      {isLoadingFiles ? (
        <div className={styles.loadingState}>
          <Spinner isMid />
        </div>
      ) : (
        <>
          <div className={styles.uploadRow}>
            <label
              htmlFor="voice-cloning-file-input"
              className={`${styles.uploadLabel} ${!canUpload ? styles.uploadLabelDisabled : ''}`}
            >
              <FeatherIcon
                strokeWidth="2px"
                icon="upload"
                size="16"
              />
              <span>{t('voiceCloning.uploadButton')}</span>
            </label>
            <span
              className={`${styles.fileCount} ${
                files.length >= MAX_FILES ? styles.fileCountMax : ''
              }`}
            >
              {t('voiceCloning.fileCount', { count: files.length })}
            </span>
          </div>

          {isUploading && (
            <div className={styles.uploadingRow}>
              <Spinner isSmall />
              <span>
                {t('voiceCloning.uploading')} {uploadingFileName}
              </span>
            </div>
          )}

          {files.length === 0 && !isUploading ? (
            <p className={styles.emptyState}>{t('voiceCloning.noFiles')}</p>
          ) : (
            <div className={styles.fileList}>
              {files.map((file) => (
                <div key={file.id} className={styles.fileItem}>
                  <button
                    className={styles.iconBtn}
                    onClick={() => handlePlayPause(file)}
                    disabled={deletingFileId === file.id}
                  >
                    <FeatherIcon
                      strokeWidth="2px"
                      icon={playingFileId === file.id ? 'pause' : 'play'}
                      size="16"
                    />
                  </button>
                  <span className={styles.fileName}>{file.fileName}</span>
                  <span className={styles.fileDuration}>
                    {formatDuration(file.duration)}
                  </span>
                  <button
                    className={styles.iconBtn}
                    onClick={() => handleDelete(file)}
                    disabled={
                      deletingFileId === file.id || isUploading
                    }
                  >
                    {deletingFileId === file.id ? (
                      <Spinner isSmall />
                    ) : (
                      <FeatherIcon
                        strokeWidth="2px"
                        icon="trash-2"
                        size="16"
                      />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}

          {error && <p className={styles.error}>{error}</p>}
          {successMessage && <p className={styles.success}>{successMessage}</p>}
        </>
      )}

      <input
        id="voice-cloning-file-input"
        ref={fileInputRef}
        type="file"
        accept=".wav,.mp3"
        onChange={handleFileChange}
        className={styles.hiddenInput}
        disabled={!canUpload}
      />
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioRef} onEnded={handleAudioEnded} />
    </div>
  );
}
